import React, { useEffect, useState } from 'react';
import { multiFactor, TotpMultiFactorGenerator, TotpSecret } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import QRCode from 'qrcode';
import { auth } from '../firebase';

export const MfaSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    (async () => {
      try {
        const session = await multiFactor(user).getSession();
        const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);
        setSecret(totpSecret);
        const otpauthUri = totpSecret.generateQrCodeUrl(user.email || 'console', 'Preventivatore Console');
        const qr = await QRCode.toDataURL(otpauthUri);
        setQrUrl(qr);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Errore generazione QR');
      }
    })();
  }, []);

  const handleVerify = async () => {
    if (!secret || !auth.currentUser) return;
    setLoading(true);
    setError(null);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
      await multiFactor(auth.currentUser).enroll(assertion, 'Authenticator app');
      navigate('/tenants', { replace: true });
    } catch (e) {
      console.error('MFA enroll failed', e);
      setError('Codice TOTP non valido. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Paper sx={{ p: 4, maxWidth: 500 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Configura MFA</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Scansiona il QR code con Google Authenticator o Authy. Poi inserisci il codice
          a 6 cifre per confermare e attivare l'autenticazione a due fattori.
        </Typography>
        {qrUrl ? (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <img src={qrUrl} alt="QR TOTP" style={{ maxWidth: 200 }} />
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mb: 2 }}><CircularProgress /></Box>
        )}
        <TextField
          label="Codice TOTP"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          fullWidth
          margin="normal"
          slotProps={{ htmlInput: { maxLength: 6, inputMode: 'numeric' } }}
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 2 }}
          disabled={loading || code.length !== 6 || !secret}
          onClick={handleVerify}
        >
          {loading ? 'Verifica...' : 'Conferma e attiva MFA'}
        </Button>
      </Paper>
    </Box>
  );
};
